import { Buffer } from 'buffer';
import { FrameCache, type DecodedFrame } from './FrameCache';
import { FRAME_COUNT, loadFrame } from './loadFrame';
import { buildHeightColors, heightRange } from '../scene/colorByHeight';

const DEFAULT_LIMIT_MB = 512;

/**
 * Read the cache budget from the URL. Honors `?maxFrameCacheMb=` and the
 * spec's literal `?MAX_FRAME_CACHE_MB=`. Defaults to 512 MB.
 * Use a small value (e.g. `?maxFrameCacheMb=32`) to emulate constrained hardware.
 */
export function readLimitMb(search = typeof window !== 'undefined' ? window.location.search : ''): number {
  const params = new URLSearchParams(search);
  const raw = params.get('maxFrameCacheMb') ?? params.get('MAX_FRAME_CACHE_MB');
  const n = raw !== null ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_LIMIT_MB;
}

/**
 * Decode a frame and measure its exact footprint.
 *
 * Typed arrays self-report via `.byteLength`; the cuboid metadata does not, so
 * we size its exact UTF-8 serialization with `Buffer.byteLength` (the `buffer`
 * package's browser polyfill).
 */
const decodeFrame: (index: number, signal: AbortSignal) => Promise<DecodedFrame> = async (index, signal) => {
  const frame = await loadFrame(index, signal);
  const colors = buildHeightColors(frame.positions, heightRange(frame.positions));
  const metaBytes = Buffer.byteLength(JSON.stringify(frame.cuboids), 'utf8');
  const byteSize = frame.positions.byteLength + colors.byteLength + metaBytes;
  return { frame, colors, byteSize };
};

export const frameCache = new FrameCache({
  limitMb: readLimitMb(),
  frameCount: FRAME_COUNT,
  windowRadius: 1,
  softWarnRatio: 0.8,
  decoder: decodeFrame,
});

// Browser "CLI": inspect live cache state from the devtools console.
if (typeof window !== 'undefined') {
  (window as unknown as { frameCache: FrameCache }).frameCache = frameCache;
}
